// Efeitos sonoros originais, sintetizados na hora com Web Audio (sem arquivos,
// sem licença de terceiros). Regras:
//  - nada toca antes do primeiro toque/clique/tecla (política de autoplay; iOS
//    exige criar/retomar o AudioContext dentro de um gesto);
//  - som e vibração podem ser desligados (preferência salva no aparelho);
//  - vibração só no Android/Chrome (navigator.vibrate) e nunca com
//    prefers-reduced-motion.

export type Sfx =
  | "click" | "buy" | "sell" | "reroll" | "xp" | "levelUp" | "error" | "tick"
  | "combatStart" | "hit" | "cast" | "faint" | "win" | "lose" | "victory" | "defeat"
  | "reward" | "chat";

const KEY = "pokechess:audio";

interface Prefs { sound: boolean; volume: number; vibrate: boolean }

function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { sound: true, volume: 0.6, vibrate: true, ...JSON.parse(raw) };
  } catch {
    /* sem storage */
  }
  return { sound: true, volume: 0.6, vibrate: true };
}

let prefs = loadPrefs();
let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let unlocked = false;
let noise: AudioBuffer | null = null;
const lastPlayed = new Map<Sfx, number>();

export function audioPrefs(): Prefs {
  return { ...prefs };
}

export function setAudioPrefs(p: Partial<Prefs>): void {
  prefs = { ...prefs, ...p };
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    /* ok */
  }
  if (master) master.gain.value = prefs.volume;
}

/** Liga o áudio no primeiro gesto do usuário (chamar uma vez no boot). */
export function installAudioUnlock(): void {
  const unlock = () => {
    unlocked = true;
    try {
      const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return;
      ctx ??= new AC();
      if (ctx.state === "suspended") void ctx.resume().catch(() => undefined);
      if (!master) {
        master = ctx.createGain();
        master.gain.value = prefs.volume;
        master.connect(ctx.destination);
      }
      // iOS: um som silencioso dentro do gesto destrava o áudio de vez
      const b = ctx.createBuffer(1, 1, 22050);
      const s = ctx.createBufferSource();
      s.buffer = b;
      s.connect(master);
      s.start(0);
    } catch {
      /* navegador sem Web Audio: segue sem som */
    }
  };
  for (const ev of ["pointerdown", "keydown", "touchend"] as const) window.addEventListener(ev, unlock, { passive: true });
  // volta do segundo plano (iOS suspende o contexto)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && ctx?.state === "suspended" && unlocked) void ctx.resume().catch(() => undefined);
  });
}

function noiseBuffer(c: AudioContext): AudioBuffer {
  if (noise) return noise;
  noise = c.createBuffer(1, c.sampleRate * 0.5, c.sampleRate);
  const d = noise.getChannelData(0);
  let x = 12345;
  for (let i = 0; i < d.length; i++) {
    x = (x * 1103515245 + 12345) & 0x7fffffff;
    d[i] = (x / 0x7fffffff) * 2 - 1;
  }
  return noise;
}

/** Nota com envelope rápido (estilo 8-bit). */
function tone(c: AudioContext, out: AudioNode, t: number, freq: number, dur: number, type: OscillatorType = "square", vol = 0.25, slideTo?: number) {
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, t);
  if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, t + dur);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(vol, t + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g).connect(out);
  o.start(t);
  o.stop(t + dur + 0.02);
}

function burst(c: AudioContext, out: AudioNode, t: number, dur: number, vol = 0.2, filterFreq = 2000) {
  const s = c.createBufferSource();
  s.buffer = noiseBuffer(c);
  const f = c.createBiquadFilter();
  f.type = "bandpass";
  f.frequency.value = filterFreq;
  const g = c.createGain();
  g.gain.setValueAtTime(vol, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  s.connect(f).connect(g).connect(out);
  s.start(t);
  s.stop(t + dur + 0.02);
}

const C5 = 523.25, E5 = 659.25, G5 = 783.99, C6 = 1046.5;

/** Intervalo mínimo entre repetições (luta tem muitos golpes por segundo). */
const MIN_GAP: Partial<Record<Sfx, number>> = { hit: 70, cast: 120, faint: 120, tick: 300, click: 40 };

export function play(name: Sfx): void {
  if (!prefs.sound || !unlocked || !ctx || !master || ctx.state !== "running") return;
  const now = performance.now();
  const gap = MIN_GAP[name];
  if (gap && now - (lastPlayed.get(name) ?? 0) < gap) return;
  lastPlayed.set(name, now);
  const c = ctx, out = master, t = c.currentTime + 0.005;
  switch (name) {
    case "click": tone(c, out, t, 880, 0.04, "square", 0.08); break;
    case "buy": tone(c, out, t, 988, 0.06, "square", 0.18); tone(c, out, t + 0.06, 1319, 0.12, "square", 0.18); break;
    case "sell": tone(c, out, t, 1319, 0.05, "triangle", 0.2); tone(c, out, t + 0.05, 880, 0.1, "triangle", 0.2); break;
    case "reroll": for (let i = 0; i < 4; i++) tone(c, out, t + i * 0.035, 600 + i * 180, 0.04, "square", 0.1); break;
    case "xp": tone(c, out, t, 440, 0.12, "triangle", 0.2, 880); break;
    case "levelUp": [C5, E5, G5, C6].forEach((f, i) => tone(c, out, t + i * 0.07, f, 0.12, "square", 0.16)); break;
    case "error": tone(c, out, t, 180, 0.15, "sawtooth", 0.12, 120); break;
    case "tick": tone(c, out, t, 1200, 0.03, "square", 0.08); break;
    case "combatStart": burst(c, out, t, 0.25, 0.12, 900); tone(c, out, t, 220, 0.25, "sawtooth", 0.1, 440); break;
    case "hit": burst(c, out, t, 0.06, 0.14, 1800); break;
    case "cast": tone(c, out, t, 660, 0.18, "sine", 0.16, 1320); burst(c, out, t, 0.12, 0.05, 4000); break;
    case "faint": tone(c, out, t, 520, 0.3, "triangle", 0.18, 90); break;
    case "win": [G5, C6].forEach((f, i) => tone(c, out, t + i * 0.09, f, 0.16, "square", 0.15)); break;
    case "lose": [392, 330].forEach((f, i) => tone(c, out, t + i * 0.12, f, 0.18, "triangle", 0.16)); break;
    case "victory": [C5, E5, G5, C6, G5, C6].forEach((f, i) => tone(c, out, t + i * 0.1, f, i === 5 ? 0.4 : 0.12, "square", 0.15)); break;
    case "defeat": [392, 349, 311, 262].forEach((f, i) => tone(c, out, t + i * 0.16, f, 0.22, "triangle", 0.16)); break;
    case "reward": [E5, G5, C6].forEach((f, i) => tone(c, out, t + i * 0.06, f, 0.1, "sine", 0.18)); break;
    case "chat": tone(c, out, t, 1568, 0.05, "sine", 0.1); tone(c, out, t + 0.06, 2093, 0.07, "sine", 0.08); break;
  }
}

/** Vibração curta (Android). Nada no iOS (não suportado) nem com movimento reduzido. */
export function buzz(pattern: number | number[]): void {
  if (!prefs.vibrate || typeof navigator.vibrate !== "function") return;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
  try {
    navigator.vibrate(pattern);
  } catch {
    /* ok */
  }
}
